import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import { isNative } from "../lib/runtime";
import { Button } from "../components/ui/Button";
import { Field, Input } from "../components/ui/Input";

// "Start your agency" page — the paid signup surface. Reached from
// the Login screen's "Start your agency" link (web only). Submits
// the email + agency name to create-checkout-session, which
// returns a Stripe-hosted Checkout URL the browser is then sent
// to. After payment, stripe-webhook provisions the user + agency
// and emails the magic link (the Welcome page sets that
// expectation).
//
// Inside the iOS Capacitor shell this page should never be
// reachable (the Login screen suppresses the link on native), but
// in case someone deep-links here we still render a friendly
// "subscribe on the web" copy instead of a useless form.
export function Subscribe({
  onGoToLogin,
}: {
  onGoToLogin: () => void;
}) {
  const [email, setEmail] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const startCheckout = useMutation({
    mutationFn: () => api.startSubscriptionCheckout({
      email: email.trim(),
      agencyName: agencyName.trim() || undefined,
    }),
    onSuccess: ({ url }) => {
      // Hand the browser off to Stripe's hosted checkout.
      window.location.href = url;
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Couldn't start checkout."),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.includes("@")) {
      setError("Enter a valid email so we can send you the welcome link.");
      return;
    }
    startCheckout.mutate();
  }

  if (isNative) {
    // iOS shell — no Subscribe form. Explain where to subscribe
    // and offer a way back to the sign-in screen.
    return (
      <div className="min-h-full bg-slate-100 px-4 py-10 flex items-center justify-center">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">
              Start your agency
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Subscriptions are managed on the web. Open a browser
              on your computer or phone and visit our website to
              provision your agency. Once you (or your supervisor)
              have invited you by email, come back here to sign in.
            </p>
          </div>
          <Button
            variant="secondary"
            className="w-full"
            onClick={onGoToLogin}
          >
            ← Back to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-100 px-4 py-8 md:py-16">
      <div className="mx-auto max-w-2xl">
        <header className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl md:text-5xl font-bold text-slate-900">
            Start your agency
          </h1>
          <p className="mt-3 text-base md:text-lg text-slate-600">
            Subscribe to provision your agency. You'll become the
            Owner; from there you can invite supervisors and officers
            and start building your schedule.
          </p>
        </header>

        <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 md:p-8">
          <form onSubmit={onSubmit} className="space-y-3">
            <Field label="Agency name (optional)">
              <Input
                type="text"
                value={agencyName}
                onChange={(e) => setAgencyName(e.target.value)}
                placeholder="Warr Acres Police Department"
                autoComplete="organization"
              />
            </Field>
            <Field label="Your email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="chief@youragency.gov"
                autoComplete="email"
                required
              />
            </Field>
            {error && <div className="text-sm text-red-700">{error}</div>}
            <Button
              type="submit"
              disabled={startCheckout.isPending}
              className="w-full"
            >
              {startCheckout.isPending ? "Opening Stripe…" : "Continue to payment"}
            </Button>
            <p className="text-xs text-slate-500 text-center pt-1">
              Secure payment by Stripe. Cancel anytime from your
              account.
            </p>
          </form>
        </section>

        <div className="mt-6 text-center text-sm text-slate-600">
          Already have an account?{" "}
          <button
            type="button"
            onClick={onGoToLogin}
            className="text-sky-700 hover:underline font-medium"
          >
            Sign in
          </button>
        </div>

        <footer className="mt-10 text-center text-xs text-slate-500">
          <a href="#/privacy" className="underline-offset-2 hover:underline">
            Privacy
          </a>
          <span className="mx-2 text-slate-400">·</span>
          <a href="#/terms" className="underline-offset-2 hover:underline">
            Terms
          </a>
          <span className="mx-2 text-slate-400">·</span>
          <a href="mailto:support@shiftmanager.app" className="underline-offset-2 hover:underline">
            Support
          </a>
        </footer>
      </div>
    </div>
  );
}
